import { toast } from "components/FluentToast";
import { Formik } from "formik";
import { useCreateFleetVehicleDefaultSetting } from "hooks/data/mutations/useCreateFleetVehicleDefaultSetting";
import { useUpdateFleetVehicleDefaultSetting } from "hooks/data/mutations/useUpdateFleetVehicleDefaultSetting";
import { delay } from "library/delay";
import moment from "moment";
import useActionConfig from "pages/fleet-form/hooks/useActionConfg";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { loadFleetWithInsurersActions, setDialog } from "store/fleetForm";
import { useSelector } from "store/hooks";
import { parseAssistanceOptions } from "utils/assistanceOptions";
import useDefaultSettingsData from "../hooks/useDefaultSettingsData";
import AssistanceOptionsFormFields, {
  assistanceOptionFieldName,
} from "./AssistanceOptionsFormFields";

type Props = {
  settingId: string;
  attributeName: string;
  isLoading: boolean;
};

// CZ: the assistance options of an insurer (texts like "SUPER", "51"; the
// client picks one of them for the requested Assistance). Only the valid from
// date, the insurer and the list of options are entered - the premium type is
// Assistance and there are no criteria.
const AssistanceOptionsForm = ({
  settingId,
  attributeName,
  isLoading,
}: Props) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const fleet = useSelector((s) => s.fleetForm.fleetWithPremiums);
  const fleetAppSettings = useSelector((s) => s.fleetForm.fleetAppSettings);
  const taxonomy = useSelector((s) => s.taxonomy);
  const assistancePremiumTypeId =
    taxonomy.PremiumType.byCode["PremiumType.ASSISTANCE"]?.id;

  const { mutate: onUpdateFleetVehicleDefaultSetting } =
    useUpdateFleetVehicleDefaultSetting();
  const { mutate: onCreateFleetVehicleDefaultSetting } =
    useCreateFleetVehicleDefaultSetting();

  const insurerOptions = useActionConfig(fleetAppSettings).insurerOptions;

  const defaultData = useDefaultSettingsData();
  const rowVersion = defaultData?.rowVersion;

  const close = () => {
    dispatch(setDialog({ type: "", isOpen: false }));
  };

  const initialValues = useMemo(() => {
    const options = parseAssistanceOptions(defaultData?.attributeValue);
    return {
      validFromDate: defaultData?.validFromDate
        ? new Date(defaultData.validFromDate)
        : new Date(),
      insurerPartnerNumber: defaultData
        ? (insurerOptions?.find(
            (item) => item.value === defaultData.insurerPartnerNumber,
          ) ?? null)
        : null,
      options: options.length > 0 ? options : [""],
    };
  }, [defaultData, insurerOptions]);

  const validate = (values) => {
    const errors = {} as Record<string, string>;
    if (!values.validFromDate) {
      errors.validFromDate = t("greco.required");
    }
    if (!values.insurerPartnerNumber?.value) {
      errors.insurerPartnerNumber = t("greco.required");
    }
    const options: string[] = values.options || [];
    if (options.length === 0) {
      errors.options = t("bfm.assistanceOptions.atLeastOneOption.error");
    }
    const seen = new Set<string>();
    options.forEach((option, index) => {
      // it is enough that the text isn't empty
      const text = (option ?? "").trim();
      if (!text) {
        errors[assistanceOptionFieldName(index)] = t("greco.required");
        return;
      }
      if (seen.has(text)) {
        errors[assistanceOptionFieldName(index)] = t(
          "bfm.assistanceOptions.duplicateOption.error",
        );
      }
      seen.add(text);
    });
    return errors;
  };

  const onSubmit = (values) => {
    // as the other default settings: the selected day at 00:00 UTC
    const localMoment = moment(values.validFromDate);
    const utcValidFromDate = new Date(
      Date.UTC(localMoment.year(), localMoment.month(), localMoment.date()),
    );

    const body = {
      validFromDate: utcValidFromDate,
      insurerGroupPolicyNumber: null,
      attributeName,
      attributeValue: JSON.stringify(
        (values.options as string[]).map((option) => option.trim()),
      ),
      premiumType: assistancePremiumTypeId,
      insurerPartnerNumber: values.insurerPartnerNumber?.value,
      // the options apply to all vehicles of the insurer
      settingCriteria: {},
    };

    const onSuccess = () => {
      toast.success(t("bfm.success"));
      close();
      dispatch(
        loadFleetWithInsurersActions.trigger({ fleetId: fleet.fleetId }),
      );
    };

    if (!!settingId) {
      onUpdateFleetVehicleDefaultSetting(
        {
          fleetId: fleet.fleetId,
          fleetDefaultSettingId: settingId,
          body: { fleetId: fleet.fleetId, ...body, rowVersion },
        },
        { onSuccess },
      );
    } else {
      onCreateFleetVehicleDefaultSetting(
        {
          fleetId: fleet.fleetId,
          body: { fleetId: fleet.fleetId, ...body },
        },
        { onSuccess },
      );
    }
  };

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize
      validate={validate}
      validateOnChange={false}
      validateOnBlur={false}
      onSubmit={async (values, { setSubmitting }) => {
        onSubmit(values);
        await delay(400);
        setSubmitting(false);
      }}
    >
      {(props) => (
        <div>
          <form onSubmit={props.handleSubmit}>
            <AssistanceOptionsFormFields
              fieldProps={props}
              insurerOptions={insurerOptions}
              isLoading={isLoading}
            />
          </form>
        </div>
      )}
    </Formik>
  );
};

export default AssistanceOptionsForm;
